import { Box, CssBaseline, StyledEngineProvider, Theme } from '@mui/material'
import { ThemeProvider } from '@mui/material/styles'
import { C1Theme } from 'c1ui'
import { C1CircularProgress } from 'c1ui/components/C1CircularProgress/C1CircularProgress'
import { C1Title } from 'components/no-ui/C1Title/C1Title'
import { AdminModeContextProvider } from 'components/no-ui/context/admin-mode'
import AlertBarProvider from 'components/no-ui/context/alertbarprovider'
import Auth from 'components/no-ui/context/auth'
import { AuthStyles } from 'components/no-ui/context/auth/styles'
import DashboardContext from 'components/no-ui/context/dashboard'
import { DrawerProvider } from 'components/no-ui/context/drawer-provider'
import { LocalStorageContextProvider } from 'components/no-ui/context/local-storage'
import { ModalContextProvider } from 'components/no-ui/context/modal-provider'
import { ScrollContainerProvider } from 'components/no-ui/context/scrollcontainer'
import { SessionStorageContextProvider } from 'components/no-ui/context/session-storage'
import { TenantConfigContextProvider } from 'components/no-ui/context/tenant-config'
import { UserContextProvider } from 'components/no-ui/context/user'
import Datadog from 'components/no-ui/error-reporting/datadog'
import { C1ThemeProviderWithDarkMode } from 'components/no-ui/theme-provider/C1ThemeProviderWithDarkMode'
import { AppBarWrapper } from 'components/pages/app-bar/AppBarWrapper'
import { SidebarWrapper } from 'components/pages/sidebar/SidebarWrapper'
import { AuthErrorFallback, PageErrorFallback } from 'lib/error-fallback'
import { NextComponentType } from 'next'
import { useRouter } from 'next/router'
import { Suspense, useEffect, useState } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { IntlProvider } from 'react-intl'
import { Provider } from 'react-redux'
import { store } from 'store/store'

declare module '@mui/styles/defaultTheme' {
    // eslint-disable-next-line @typescript-eslint/no-empty-interface
    interface DefaultTheme extends Theme { }
}
const Loading = (): React.ReactElement => {
    const [animate, setAniminate] = useState(false)

    useEffect(() => {
        const timeout = setTimeout(setAniminate, 1500, true)
        return () => clearTimeout(timeout)
    }, [])

    return (
        <Box sx={AuthStyles.loadingWrapper}>
            {animate && <C1CircularProgress />}
        </Box>
    )
}

function App({
    Component,
    pageProps,
}: {
    Component: NextComponentType
    pageProps: Record<string, unknown>
}): React.ReactElement {
    const router = useRouter()
    const { locale, defaultLocale } = router

    useEffect(() => {
        // This removes styles applied server-side. Without server-side styles material-ui breaks in strange ways.
        const jssStyles = document.querySelector('#jss-server-side')
        if (jssStyles && jssStyles.parentElement) {
            jssStyles.parentElement.removeChild(jssStyles)
        }
    }, [])

    // Unauthenticated pages - no C1 auth required (accounts, vault delivery, external secrets)
    const unauthPaths = ['/accounts', '/vault-delivery', '/ext/secrets/share']
    if (unauthPaths.some((p) => router.pathname.startsWith(p))) {
        return (
            <>
                <StyledEngineProvider injectFirst>
                    <ThemeProvider theme={C1Theme}>
                        <CssBaseline />
                        <IntlProvider locale={locale as string} defaultLocale={defaultLocale}>
                            <div className="c1">
                                <main>
                                    <ErrorBoundary FallbackComponent={AuthErrorFallback}>
                                        <Component {...pageProps} />
                                    </ErrorBoundary>
                                </main>
                            </div>
                        </IntlProvider>
                    </ThemeProvider>
                </StyledEngineProvider>
            </>
        )
    }

    return (
        <>
            <StyledEngineProvider injectFirst>
                <Provider store={store}>
                    <ThemeProvider theme={C1Theme}>
                        <CssBaseline />
                        <IntlProvider locale={locale as string} defaultLocale={defaultLocale}>
                            <C1Title noSuffix title="ConductorOne" />
                            <div className="c1">
                                <Auth>
                                    <ErrorBoundary FallbackComponent={AuthErrorFallback}>
                                        <UserContextProvider>
                                            <C1ThemeProviderWithDarkMode>
                                                <TenantConfigContextProvider>
                                                    <Suspense fallback={<Loading />}>
                                                        <DashboardContext>
                                                            <Datadog />
                                                            <AlertBarProvider maxAlerts={1}>
                                                                <DrawerProvider>
                                                                    <ModalContextProvider>
                                                                        <LocalStorageContextProvider>
                                                                            <SessionStorageContextProvider>
                                                                                <AdminModeContextProvider>
                                                                                    <AppBarWrapper />
                                                                                    <SidebarWrapper />
                                                                                    <ScrollContainerProvider noStylePage={false}>
                                                                                        <ErrorBoundary
                                                                                            FallbackComponent={PageErrorFallback}
                                                                                        >
                                                                                            <Suspense fallback={<Loading />}>
                                                                                                <Component {...pageProps} />
                                                                                            </Suspense>
                                                                                        </ErrorBoundary>
                                                                                    </ScrollContainerProvider>
                                                                                </AdminModeContextProvider>
                                                                            </SessionStorageContextProvider>
                                                                        </LocalStorageContextProvider>
                                                                    </ModalContextProvider>
                                                                </DrawerProvider>
                                                            </AlertBarProvider>
                                                        </DashboardContext>
                                                    </Suspense>
                                                </TenantConfigContextProvider>
                                            </C1ThemeProviderWithDarkMode>
                                        </UserContextProvider>
                                    </ErrorBoundary>
                                </Auth>
                            </div>
                        </IntlProvider>
                    </ThemeProvider>
                </Provider>
            </StyledEngineProvider>
        </>
    )
}
export default App
